import { useImperativeHandle, useState, forwardRef, useCallback } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Lock, AlertCircle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/Input';
import { paymentGatewayApi } from '@/services/paymentGatewayApi';

const CARD_ELEMENT_OPTIONS = {
  // Stripe Elements iframe doesn't inherit Tailwind — pass tokens via style.base / .invalid
  // so the field reads against any background and respects our type scale.
  style: {
    base: {
      iconColor: 'hsl(263 90% 65%)',
      color: 'hsl(220 18% 22%)',
      fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '15px',
      lineHeight: '24px',
      '::placeholder': { color: 'hsl(220 14% 56%)' },
    },
    invalid: {
      iconColor: 'hsl(0 84% 60%)',
      color: 'hsl(0 84% 45%)',
    },
  },
  hidePostalCode: false,
};

export interface StripeCardFormHandle {
  /** Confirm payment. Returns true on success, false on user-correctable failure, throws on hard error. */
  submit: () => Promise<{ ok: true; paymentIntentId: string } | { ok: false; message: string }>;
}

export interface StripeCardFormProps {
  /** Amount in major units (e.g. 29.00) — sent to backend, which converts to minor units. */
  amount: number;
  currency: string;
  customerEmail?: string;
  customerId?: string;
  subscriptionId?: string;
  planId?: string;
  invoiceId?: string;
  description?: string;
  /** Sets billing details on the PaymentMethod that Stripe creates. */
  billingName?: string;
  /** Disable the form (e.g. while the parent is loading plan data). */
  disabled?: boolean;
  /** Fired on every Stripe Elements change — useful for showing inline validation state. */
  onCardStateChange?: (state: { complete: boolean; error?: string }) => void;
}

/**
 * Stripe Elements card-input form.
 *
 * <p>The parent component owns the "Pay" button — it calls {@code submit()} via the
 * forwarded ref. Why: the parent already has plan summary, coupons, billing details,
 * processing animation, and progress bar logic. Pulling all that into this component
 * would couple too tightly. The ref pattern keeps the boundary clean.</p>
 *
 * <p>The actual flow this component drives:
 * <ol>
 *   <li>Parent calls {@code submit()}.</li>
 *   <li>This component calls the backend {@code POST /api/v1/payments/create-intent}.</li>
 *   <li>Stripe.js {@code confirmCardPayment(clientSecret, { payment_method: { card } })}.</li>
 *   <li>Returns {ok, paymentIntentId} — parent finalises subscription/UX.</li>
 *   <li>Stripe webhook independently updates persisted state on the backend.</li>
 * </ol>
 *
 * <p>3D Secure is handled inline by Stripe.js — no extra code needed.</p>
 */
export const StripeCardForm = forwardRef<StripeCardFormHandle, StripeCardFormProps>((props, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | undefined>(undefined);
  const [billingPostal, setBillingPostal] = useState('');

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        if (!stripe || !elements) {
          return { ok: false, message: 'Payment system is initialising — please try again in a moment.' };
        }
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          return { ok: false, message: 'Card field is not ready.' };
        }
        if (!cardComplete) {
          return { ok: false, message: 'Please complete your card details.' };
        }

        // 1) Create PaymentIntent on the backend.
        let clientSecret: string | undefined;
        try {
          const intent = await paymentGatewayApi.createPaymentIntent({
            amount: props.amount,
            currency: props.currency,
            customerEmail: props.customerEmail,
            customerId: props.customerId,
            subscriptionId: props.subscriptionId,
            planId: props.planId,
            invoiceId: props.invoiceId,
            description: props.description,
            gateway: 'STRIPE',
          });
          clientSecret = intent.clientSecret;
        } catch (e) {
          const message = e instanceof Error ? e.message : 'Could not start payment';
          return { ok: false, message };
        }
        if (!clientSecret) {
          return { ok: false, message: 'Payment intent has no client secret — gateway may not be configured.' };
        }

        // 2) Confirm card payment client-side. 3D Secure runs automatically.
        const confirm = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: props.billingName,
              email: props.customerEmail,
              address: billingPostal ? { postal_code: billingPostal } : undefined,
            },
          },
        });
        if (confirm.error) {
          const message = confirm.error.message ?? 'Payment failed';
          setCardError(message);
          return { ok: false, message };
        }
        if (confirm.paymentIntent && confirm.paymentIntent.status === 'succeeded') {
          return { ok: true, paymentIntentId: confirm.paymentIntent.id };
        }
        return {
          ok: false,
          message: `Payment is ${confirm.paymentIntent?.status ?? 'pending'} — please refresh in a moment.`,
        };
      },
    }),
    [stripe, elements, cardComplete, billingPostal, props],
  );

  const handleCardChange = useCallback(
    (event: { complete: boolean; error?: { message: string } }) => {
      setCardComplete(event.complete);
      setCardError(event.error?.message);
      props.onCardStateChange?.({ complete: event.complete, error: event.error?.message });
    },
    [props],
  );

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="stripe-card-element" className="flex items-center justify-between">
          <span>Card details</span>
          <span className="text-xs font-normal text-muted-foreground flex items-center gap-1">
            <Lock className="h-3 w-3" /> Secured by Stripe
          </span>
        </Label>
        <div
          id="stripe-card-element"
          className={`mt-1.5 rounded-lg border bg-background px-4 py-3.5 transition-colors ${
            cardError
              ? 'border-destructive ring-1 ring-destructive/30'
              : cardComplete
                ? 'border-emerald-500/50'
                : 'border-input focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/30'
          }`}
        >
          <CardElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardChange} />
        </div>
        {cardError && (
          <p className="mt-1.5 text-xs text-destructive flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3 shrink-0" />
            {cardError}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="stripe-cardholder">Cardholder name</Label>
          <Input
            id="stripe-cardholder"
            value={props.billingName ?? ''}
            readOnly
            placeholder="Name on card"
            className="bg-muted/40"
          />
        </div>
        <div>
          <Label htmlFor="stripe-postal">Billing postal code</Label>
          <Input
            id="stripe-postal"
            value={billingPostal}
            onChange={(e) => setBillingPostal(e.target.value)}
            placeholder="ZIP / Postal code"
            inputMode="text"
            disabled={props.disabled}
          />
        </div>
      </div>
    </div>
  );
});
StripeCardForm.displayName = 'StripeCardForm';
